/**
 * Lightweight JSDoc type definitions for the Categorical Deep-Chain Reasoner.
 *
 * Mirrors the dataclasses in
 * `GraphBuilder/backend/app/snn_engine/chain_reasoner.py` and the schema in
 * `GraphBuilder/backend/app/process_engine/case_evidence_schema.json`.
 *
 * These shapes are intentionally JSDoc-only (not real .ts types) to avoid
 * pulling TypeScript into the existing CRA build. Components import the
 * named types via `@typedef` references in their JSDoc comments.
 */

/**
 * @typedef {Object} CaseEvidenceFrame
 * @property {string} [case_id]
 * @property {string} [domain]
 * @property {{name?: string, type?: string, country?: string, address?: string}} [master_customer]
 * @property {Array<EvidenceEntity>} [entities]
 * @property {Array<EvidenceRelationship>} [relationships]
 * @property {Array<EvidenceTransaction>} [transactions]
 * @property {Object} [screening]
 * @property {Object} [kyc]
 * @property {Array<Object>} [regulatory_inquiries]
 * @property {Array<Object>} [adverse_media]
 * @property {Array<Object>} [auditor_notes]
 * @property {Array<Object>} [alerts]
 * @property {Array<string>} [extractor_warnings]
 */

/**
 * @typedef {Object} EvidenceEntity
 * @property {string} [entity_id]
 * @property {string} [name]
 * @property {string} [type]
 * @property {string} [country]
 * @property {string} [address]
 * @property {Array<string>} [aliases]
 * @property {Array<string>} [predecessor_names]
 */

/**
 * @typedef {Object} EvidenceRelationship
 * @property {string} [relationship_id]
 * @property {string} [from_entity]
 * @property {string} [to_entity]
 * @property {string} [type]
 * @property {string} [evidence_quote]
 */

/**
 * @typedef {Object} EvidenceTransaction
 * @property {string} [txn_id]
 * @property {string} [date]
 * @property {number} [amount_usd]
 * @property {string} [currency]
 * @property {string} [originator]
 * @property {string} [beneficiary]
 * @property {string} [counterparty_country]
 * @property {string} [purpose_stated]
 */

/**
 * @typedef {Object} ChainPredicateView
 * @property {('rule'|'case_field'|'case_nl'|'case')} kind
 * @property {string} [anchor]
 * @property {string} [graph_ref]
 * @property {string} [case_path]
 * @property {string} [op]
 * @property {string} [case_query]
 * @property {string} [evidence_quote]
 * @property {number} [confidence]
 * @property {boolean} [fired]
 */

/**
 * @typedef {Object} FiredViolationInstance
 * @property {Object} binding
 * @property {string} [binding_kind]
 * @property {boolean} fired
 * @property {number} [score]
 * @property {Array<ChainPredicateView>} [fired_predicates]
 * @property {Array<ChainPredicateView>} [unmet_predicates]
 */

/**
 * @typedef {Object} FiredViolation
 * @property {string} pack_id
 * @property {string} [pack_name]
 * @property {string} [domain]
 * @property {('CRITICAL'|'HIGH'|'MEDIUM'|'LOW')} severity
 * @property {number} [weighted_score]
 * @property {boolean} [fired]
 * @property {Array<FiredViolationInstance>} instances
 * @property {Array<string>} [chain_ids]
 */

/**
 * @typedef {Object} ViolationsReport
 * @property {string} [domain]
 * @property {string} [decision]
 * @property {string} [aggregate_decision]
 * @property {number} [aggregate_score]
 * @property {number} [score]
 * @property {number} [total_packs_fired]
 * @property {number} [total_packs_loaded]
 * @property {number} [total_instances_fired]
 * @property {Array<FiredViolation>} [violations]
 * @property {Array<string>} [fired_pack_ids]
 * @property {string} [evidence_summary]
 * @property {Array<Object>} [skipped_packs]
 * @property {Object} [aggregate_rule_trace]
 */

/**
 * @typedef {Object} ChainPackSummary
 * @property {string} pack_id
 * @property {string} [name]
 * @property {string} [domain]
 * @property {('CRITICAL'|'HIGH'|'MEDIUM'|'LOW')} [severity]
 * @property {string} [description]
 * @property {number} n_chains
 * @property {Array<{chain_id: string, chain_name: string, n_predicates: number, predicates: Array<ChainPredicateView>, iterate_over: Object}>} chains
 * @property {string} file
 * @property {string} [last_modified]
 */

/**
 * @typedef {Object} PackRegistrySummary
 * @property {string} file
 * @property {Object<string, Array<string>>} domains
 * @property {string} [last_modified]
 */

/**
 * @typedef {Object} DecisionPolicySummary
 * @property {string} file
 * @property {Object<string, number>} severity_weights
 * @property {Array<{if?: string, then?: string, else?: string}>} rules
 * @property {string} [last_modified]
 */

/**
 * @typedef {Object} PackValidationIssue
 * @property {('error'|'warning')} severity
 * @property {string} code
 * @property {string} message
 * @property {string} [path]
 */

/**
 * @typedef {Object} PackValidationResult
 * @property {boolean} ok
 * @property {('pack'|'registry'|'policy'|null)} kind
 * @property {Object|null} parsed
 * @property {Array<PackValidationIssue>} errors
 * @property {Array<PackValidationIssue>} warnings
 * @property {Array<string>} case_literals_found
 */

/**
 * @typedef {Object} GraphTagEntry
 * @property {string} [graph_id]
 * @property {string} graph_ref
 * @property {string} [graph_name]
 * @property {('foundational'|'case'|'ontology')} tag
 * @property {Array<string>} [covers_categories]
 * @property {string} [source_doc]
 */

/**
 * @typedef {Object} FoundationalBuildStatus
 * @property {string} job_id
 * @property {('queued'|'running'|'completed'|'failed')} status
 * @property {string} [started_at]
 * @property {string} [completed_at]
 * @property {Array<string>} only
 * @property {boolean} rebuild_all
 * @property {number|null} return_code
 * @property {string|null} error
 * @property {Array<string>} log_tail
 */

export const SEVERITY_RANK = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  UNKNOWN: 0,
};

export const PACK_KINDS = ['pack', 'registry', 'policy'];

export const TAG_VALUES = ['foundational', 'case', 'ontology'];
